import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import classes from './auth.module.css';
import logo from '../../../images/logo.jpg';
import {loginWebUser, registerWebUser} from '../../../utils/api';

function IconEye({className}) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            width="20"
            height="20"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    );
}

function IconEyeSlash({className}) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            width="20"
            height="20"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
    );
}

const Auth = () => {
    const navigate = useNavigate();
    const [mode, setMode] = useState('login');
    const [logoBroken, setLogoBroken] = useState(false);

    const [loginUsername, setLoginUsername] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    const [regLogin, setRegLogin] = useState('');
    const [regPassword, setRegPassword] = useState('');
    const [regPassword2, setRegPassword2] = useState('');
    const [showRegPassword, setShowRegPassword] = useState(false);
    const [showRegPassword2, setShowRegPassword2] = useState(false);

    const [notice, setNotice] = useState(null);
    const [loginSubmitting, setLoginSubmitting] = useState(false);
    const [registerSubmitting, setRegisterSubmitting] = useState(false);

    const goLogin = () => {
        setNotice(null);
        setMode('login');
        setShowRegPassword(false);
        setShowRegPassword2(false);
    };

    const goRegister = () => {
        setNotice(null);
        setMode('register');
        setShowRegPassword(false);
        setShowRegPassword2(false);
    };

    const onLoginSubmit = async (e) => {
        e.preventDefault();

        setNotice(null);

        const login = loginUsername.trim();
        if (!login) {
            setNotice({variant: 'error', text: 'Введите логин'});
            return;
        }
        if (loginPassword.length < 8) {
            setNotice({variant: 'error', text: 'Пароль должен быть не короче 8 символов'});
            return;
        }

        setLoginSubmitting(true);
        try {
            const data = await loginWebUser({login, password: loginPassword});
            if (data?.token) {
                localStorage.setItem('efv_web_token', data.token);
            }
            setLoginPassword('');
            setNotice({variant: 'success', text: `Вы вошли как «${data?.site_login || login}».`});
            // Go to cabinet so user can manage configs.
            navigate('/cabinet');
        } catch (err) {
            const text =
                err && err.message
                    ? err.message
                    : 'Не удалось связаться с сервером. Проверьте интернет и попробуйте снова.';
            setNotice({variant: 'error', text});
        } finally {
            setLoginSubmitting(false);
        }
    };

    const onRegisterSubmit = async (e) => {
        e.preventDefault();
        setNotice(null);

        const login = regLogin.trim();
        if (!login) {
            setNotice({variant: 'error', text: 'Введите логин'});
            return;
        }
        if (regPassword.length < 8) {
            setNotice({variant: 'error', text: 'Пароль должен быть не короче 8 символов'});
            return;
        }
        if (regPassword !== regPassword2) {
            setNotice({variant: 'error', text: 'Пароли не совпадают'});
            return;
        }

        setRegisterSubmitting(true);
        try {
            const data = await registerWebUser({login, password: regPassword});
            setLoginUsername(data.site_login || login);
            setLoginPassword('');
            setRegPassword('');
            setRegPassword2('');
            setRegLogin('');
            setMode('login');
            setNotice({
                variant: 'success',
                text: `Аккаунт «${data.site_login}» создан.`,
            });
        } catch (err) {
            const text =
                err && err.message
                    ? err.message
                    : 'Не удалось связаться с сервером. Проверьте интернет и попробуйте снова.';
            setNotice({variant: 'error', text});
        } finally {
            setRegisterSubmitting(false);
        }
    };

    return (
        <div className={classes.page}>
            <header className={classes.topBar}>
                <Link to="/" className={classes.brandLink}>
                    {!logoBroken ? (
                        <img
                            className={classes.logoImg}
                            src={logo}
                            alt=""
                            onError={() => setLogoBroken(true)}
                        />
                    ) : null}
                    <span className={classes.brandText}>
                        <span className={classes.logoAccent}>EasyFast</span>
                        <span className={classes.brandSuffix}>VPN</span>
                    </span>
                </Link>
                <Link to="/" className={classes.backLink}>
                    На главную
                </Link>
            </header>

            <main className={classes.main}>
                <div className={classes.card}>
                    <h1 className={classes.cardTitle}>
                        {mode === 'login' ? 'Вход' : 'Регистрация'}
                    </h1>
                    <div className={classes.tabs} role="tablist" aria-label="Режим">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={mode === 'login'}
                            className={`${classes.tab} ${mode === 'login' ? classes.tabActive : ''}`}
                            onClick={goLogin}
                        >
                            Вход
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={mode === 'register'}
                            className={`${classes.tab} ${mode === 'register' ? classes.tabActive : ''}`}
                            onClick={goRegister}
                        >
                            Регистрация
                        </button>
                    </div>

                    {notice ? (
                        <div
                            className={
                                notice.variant === 'success'
                                    ? classes.noticeSuccess
                                    : notice.variant === 'info'
                                      ? classes.noticeInfo
                                      : classes.noticeError
                            }
                            role="alert"
                        >
                            {notice.text}
                        </div>
                    ) : null}

                    {mode === 'login' ? (
                        <form className={classes.form} onSubmit={onLoginSubmit} noValidate>
                            <div className={classes.field}>
                                <label className={classes.label} htmlFor="auth-login">
                                    Логин
                                </label>
                                <input
                                    id="auth-login"
                                    name="login"
                                    className={classes.input}
                                    type="text"
                                    autoComplete="username"
                                    placeholder="Ваш логин"
                                    value={loginUsername}
                                    onChange={(e) => setLoginUsername(e.target.value)}
                                />
                            </div>
                            <div className={classes.field}>
                                <label className={classes.label} htmlFor="auth-password">
                                    Пароль
                                </label>
                                <input
                                    id="auth-password"
                                    name="password"
                                    className={classes.input}
                                    type="password"
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                    value={loginPassword}
                                    onChange={(e) => setLoginPassword(e.target.value)}
                                />
                            </div>
                            <button
                                type="submit"
                                className={classes.submitBtn}
                                disabled={loginSubmitting}
                            >
                                Войти
                            </button>
                        </form>
                    ) : (
                        <form className={classes.form} onSubmit={onRegisterSubmit} noValidate>
                            <div className={classes.field}>
                                <label className={classes.label} htmlFor="reg-login">
                                    Логин
                                </label>
                                <input
                                    id="reg-login"
                                    name="login"
                                    className={classes.input}
                                    type="text"
                                    autoComplete="username"
                                    placeholder="Придумайте логин"
                                    value={regLogin}
                                    onChange={(e) => setRegLogin(e.target.value)}
                                    disabled={registerSubmitting}
                                />
                                <p className={classes.hint}>Латиница, цифры и «_», от 3 символов</p>
                            </div>
                            <div className={classes.field}>
                                <label className={classes.label} htmlFor="reg-password">
                                    Пароль
                                </label>
                                <div className={classes.passwordWrap}>
                                    <input
                                        id="reg-password"
                                        name="password"
                                        className={classes.input}
                                        type={showRegPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        placeholder="••••••••"
                                        value={regPassword}
                                        onChange={(e) => setRegPassword(e.target.value)}
                                        disabled={registerSubmitting}
                                    />
                                    <button
                                        type="button"
                                        className={classes.passwordToggle}
                                        onClick={() => setShowRegPassword((v) => !v)}
                                        disabled={registerSubmitting}
                                        aria-label={showRegPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                    >
                                        {showRegPassword ? <IconEyeSlash /> : <IconEye />}
                                    </button>
                                </div>
                            </div>
                            <div className={classes.field}>
                                <label className={classes.label} htmlFor="reg-password2">
                                    Пароль ещё раз
                                </label>
                                <div className={classes.passwordWrap}>
                                    <input
                                        id="reg-password2"
                                        name="passwordConfirm"
                                        className={classes.input}
                                        type={showRegPassword2 ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        placeholder="Повторите пароль"
                                        value={regPassword2}
                                        onChange={(e) => setRegPassword2(e.target.value)}
                                        disabled={registerSubmitting}
                                    />
                                    <button
                                        type="button"
                                        className={classes.passwordToggle}
                                        onClick={() => setShowRegPassword2((v) => !v)}
                                        disabled={registerSubmitting}
                                        aria-label={showRegPassword2 ? 'Скрыть пароль' : 'Показать пароль'}
                                    >
                                        {showRegPassword2 ? <IconEyeSlash /> : <IconEye />}
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                className={classes.submitBtn}
                                disabled={registerSubmitting}
                            >
                                {registerSubmitting ? 'Отправка…' : 'Зарегистрироваться'}
                            </button>
                        </form>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Auth;
